import { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { getActiveToken, isExpiredTokenError, refreshSession } from '../utils/sessionToken.util';

const currentPath = window.location.hostname;
const baseURL = currentPath?.split('.')?.includes('lms2')
  ? process.env.REACT_APP_API_2_URL
  : process.env.REACT_APP_API_URL;

const HEARTBEAT_INTERVAL = 30000; // 30 seconds

// Generate or retrieve tabId per browser tab (persists across re-renders, unique per tab)
function getTabId() {
  let tabId = sessionStorage.getItem('elearning-tab-id');
  if (!tabId) {
    tabId = crypto.randomUUID();
    sessionStorage.setItem('elearning-tab-id', tabId);
  }
  return tabId;
}

// Cờ takeover ONE-SHOT (sessionStorage — per tab): được set khi có hành
// động CON NGƯỜI (vừa login ở ElearningStudentLoginPage / bấm "Tiếp tục
// học trên thiết bị này" ở SessionKickedOverlay). Đọc là xoá ngay để
// reconnect TỰ ĐỘNG sau đó không mang cờ → không giành lại phiên của
// thiết bị khác (chống kick war). Key phải khớp SessionKickedOverlay.
export const TAKEOVER_FLAG_KEY = 'elearning-session-takeover';
function consumeTakeoverFlag() {
  try {
    if (sessionStorage.getItem(TAKEOVER_FLAG_KEY) === '1') {
      sessionStorage.removeItem(TAKEOVER_FLAG_KEY);
      return true;
    }
  } catch (_) { /* noop */ }
  return false;
}

/**
 * Hook quản lý phiên học e-learning qua Socket.io
 * Auth: token (JWT sessionToken, ưu tiên) + moodleToken (fallback)
 * Server tự xác định drivingStudentId từ token/moodleToken
 */
export default function useElearningSession() {
  const [isConnected, setIsConnected] = useState(false);
  const [isKicked, setIsKicked] = useState(false);
  const [isTerminated, setIsTerminated] = useState(false);
  const [isDenied, setIsDenied] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);

  const socketRef = useRef(null);
  const heartbeatRef = useRef(null);

  const cleanup = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  }, []);

  useEffect(() => {
    // After Phase 1A login proxy lands, sessionToken alone is sufficient.
    // Keep moodleToken as a transition fallback so tabs that pre-date the
    // proxy still connect — Phase 2D step 5 strips it entirely.
    const sessionToken = localStorage.getItem('sessionToken');
    const moodleToken = localStorage.getItem('moodleToken');
    if (!sessionToken && !moodleToken) return;

    let cancelled = false;
    // Chống loop refresh: chỉ thử refresh 1 lần cho 1 vòng connect_error.
    let refreshAttempted = false;

    const connect = async () => {
      try {
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        const deviceFingerprint = result.visitorId;
        const tabId = getTabId();

        if (cancelled) return;

        const socket = io(`${baseURL}/elearning-session`, {
          // Đọc token MỚI NHẤT từ localStorage (qua getActiveToken) thay vì
          // capture lúc mount — để hưởng sessionToken mà axiosClient vừa
          // refresh. socket.auth được đọc lại mỗi lần (re)connect.
          auth: (cb) => cb({
            token: getActiveToken(),
            moodleToken: localStorage.getItem('moodleToken'),
            deviceFingerprint,
            tabId,
            // One-shot: chỉ true đúng 1 lần connect ngay sau hành động
            // con người; auto-reconnect sau đó luôn false.
            takeover: consumeTakeoverFlag(),
          }),
          transports: ['websocket'],
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 3000,
        });

        socketRef.current = socket;

        socket.on('session-connected', (data) => {
          setIsConnected(true);
          setSessionId(data?.sessionId || null);
          setError(null);

          // Lưu JWT token để lần kết nối sau không cần gọi Moodle API
          if (data?.token) {
            localStorage.setItem('sessionToken', data.token);
          }

          // Start heartbeat
          if (heartbeatRef.current) clearInterval(heartbeatRef.current);
          heartbeatRef.current = setInterval(() => {
            socket.emit('heartbeat');
          }, HEARTBEAT_INTERVAL);
        });

        socket.on('heartbeat-ack', () => {
          // Heartbeat acknowledged — connection is healthy
        });

        socket.on('session-kicked', (data) => {
          setIsKicked(true);
          setReason(data?.reason || 'Phiên học đã được mở ở thiết bị/tab khác');
          if (heartbeatRef.current) {
            clearInterval(heartbeatRef.current);
            heartbeatRef.current = null;
          }
        });

        socket.on('session-terminated', (data) => {
          setIsTerminated(true);
          setReason(data?.reason || 'Phiên học đã bị kết thúc');
          if (heartbeatRef.current) {
            clearInterval(heartbeatRef.current);
            heartbeatRef.current = null;
          }
        });

        // Bị từ chối quyền học: tài khoản đang có phiên trên thiết bị/tab
        // khác (session-ownership). Dừng hẳn socket — KHÔNG để reconnect
        // churn; overlay sẽ cho học viên chọn "Tiếp tục học trên thiết bị
        // này" (set takeover flag + reload) hoặc rời đi.
        socket.on('session-denied', (data) => {
          setIsDenied(true);
          setReason(data?.reason || 'Tài khoản đang có phiên học trên thiết bị/tab khác.');
          cleanup();
        });

        socket.on('session-error', (data) => {
          setError(data?.message || 'Lỗi phiên học');
        });

        socket.on('connect_error', async (err) => {
          setIsConnected(false);
          const msg = err?.message || '';

          // Lỗi mạng thường (không phải token) → để socket auto-reconnect.
          if (!isExpiredTokenError(msg)) {
            setError(msg || 'Lỗi kết nối');
            return;
          }

          // Token hết hạn/không hợp lệ. KHÔNG logout thẳng (bug cũ): trước
          // tiên thử refresh session — vì axiosClient có thể chưa kịp refresh,
          // hoặc token trong localStorage đã hết hạn nhưng refreshToken (30d)
          // vẫn còn. Refresh THÀNH CÔNG → socket reconnect tự đọc token mới
          // (auth callback). Chỉ logout khi refresh fail / không có refreshToken.
          if (!refreshAttempted) {
            refreshAttempted = true;
            try {
              await refreshSession();          // lưu sessionToken mới vào localStorage
              if (cancelled) return;
              socket.connect();                // reconnect: auth callback đọc token mới
              return;
            } catch (_) {
              // refresh fail → rơi xuống logout bên dưới.
            }
          }

          // Hết cách cứu (không refreshToken / refresh fail / đã thử rồi) → logout.
          cleanup();
          try { localStorage.removeItem('moodleToken'); } catch (_) { /* noop */ }
          try { localStorage.removeItem('moodleSiteInfo'); } catch (_) { /* noop */ }
          try { localStorage.removeItem('sessionToken'); } catch (_) { /* noop */ }
          try { localStorage.removeItem('refreshToken'); } catch (_) { /* noop */ }
          window.location.href = '/elearning/login';
        });

        socket.on('disconnect', () => {
          setIsConnected(false);
        });
      } catch (err) {
        console.error('Error initializing elearning session:', err);
        setError('Không thể khởi tạo phiên học');
      }
    };

    connect();

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [cleanup]);

  return { isConnected, isKicked, isTerminated, isDenied, sessionId, reason, error };
}
